import React from 'react'
import NetworkStatus from './components/NetworkStatus'
import { useSupabaseAuth } from './lib/useSupabaseAuth.jsx'
import { AuthErrorBoundary } from './components/AuthErrorBoundary'
import TokenErrorHandler from './components/TokenErrorHandler'
import { ReactQueryDataProvider } from './lib/ReactQueryDataProvider.jsx'
import { NotificationProvider, NotificationContainer } from './components/NotificationSystem'
import { StripeAccountStatusMonitor } from './components/StripeAccountStatusMonitor'
import MainRouter from './components/MainRouter'
import DemoProvider from './components/demo/DemoProvider'
import { OutOfCreditsProvider } from './contexts/OutOfCreditsContext'
import { ResponsiveProvider } from './features/dashboard/components/ResponsiveDesign'
import { logger } from './lib/logger'
import './lib/logManager' // Make log management functions available globally

// Enhanced Error Boundary Component with mobile compatibility
class ErrorBoundary extends React.Component {
	constructor(props) {
		super(props);
		this.state = { hasError: false, error: null };
	}

	static getDerivedStateFromError(error) {
		// Only filter out the very specific adoptableArticle error
		const errorMessage = error?.message || '';
		if (errorMessage.includes('adoptableArticle') && errorMessage.includes('textcontent')) {
			console.warn('[ErrorBoundary] Filtered third-party error:', errorMessage);
			return { hasError: false };
		}
		return { hasError: true, error };
	}

	componentDidCatch(error, errorInfo) {
		// Log all errors except the specific adoptableArticle error
		const errorMessage = error?.message || '';
		if (!(errorMessage.includes('adoptableArticle') && errorMessage.includes('textcontent'))) {
			console.error('[ErrorBoundary] Error caught:', error, errorInfo);
		}
	}

	render() {
		if (this.state.hasError) {
			return (
				<div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
					<div className="text-center max-w-md mx-auto">
						<div className="mb-6">
							<div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
								<svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
								</svg>
							</div>
							<h1 className="text-xl font-bold text-primary mb-2">Something went wrong</h1>
							<p className="text-secondary text-sm">The application encountered an error.</p>
						</div>
						<div className="space-y-3">
							<button 
								onClick={() => window.location.reload()}
								className="w-full bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
							>
								Refresh Page
							</button>
							<button 
								onClick={() => this.setState({ hasError: false, error: null })}
								className="w-full bg-gray-200 hover:bg-gray-300 text-secondary px-6 py-3 rounded-lg font-medium transition-colors"
							>
								Try Again
							</button>
						</div>
					</div>
				</div>
			);
		}

		return this.props.children;
	}
}

// Main router component that handles both public and protected routes
const AppShell = React.memo(() => {
	return <MainRouter />
})

// Mobile-optimized Gate component
const Gate = React.memo(() => {
	const { isAuthenticated, authReady, isSigningUp } = useSupabaseAuth()

	logger.app('Gate component - authReady:', authReady, 'isAuthenticated:', isAuthenticated)

	// Show loading state while auth is initializing. Not on `loading`: sign-in
	// sets it, and unmounting the tree mid-attempt threw away the sign-in
	// modal's error before it could render (see MainRouter).
	if (!authReady && !isSigningUp) {
		logger.app('Showing auth loading state...')
		return (
			<div className="min-h-screen flex items-center justify-center bg-page p-4">
				<div className="text-center">
					<div className="w-8 h-8 rounded-full border-4 border-default border-t-blue-500 animate-spin mb-4 mx-auto"></div>
					<p className="text-muted text-sm">Initializing...</p>
				</div>
			</div>
		)
	}

	// Show public routes when not authenticated and not signing up
	if (!isAuthenticated && !isSigningUp) {
		return (
			<ResponsiveProvider>
				<MainRouter />
			</ResponsiveProvider>
		)
	}
	
	// Show authenticated app
	return (
		<ResponsiveProvider>
			<NotificationProvider>
				<NotificationContainer position="top-right" />
				<ReactQueryDataProvider>
					<DemoProvider>
						<OutOfCreditsProvider>
							{/* Monitor must live INSIDE ReactQueryDataProvider —
							    it calls useStripeAccountStatus which now uses
							    React Query. Mounting it outside throws
							    "No QueryClient set". */}
							<StripeAccountStatusMonitor />
							<AppShell />
						</OutOfCreditsProvider>
					</DemoProvider>
				</ReactQueryDataProvider>
			</NotificationProvider>
		</ResponsiveProvider>
	)
})

const App = React.memo(() => {
	logger.app('App component rendering...')
	
	// Mobile-specific initialization check
	React.useEffect(() => {
		logger.app('App mounted successfully')
		
		// Ensure the app is visible on mobile
		const rootElement = document.getElementById('root')
		if (rootElement) {
			rootElement.style.display = 'block'
			rootElement.style.visibility = 'visible'
		}
		
		// Mobile-specific viewport handling
		if (window.innerWidth < 640) {
			// Ensure proper mobile viewport
			const viewport = document.querySelector('meta[name="viewport"]')
			if (viewport) {
				viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no')
			}
			
			// Set CSS custom property for viewport height
			const setViewportHeight = () => {
				const vh = window.innerHeight * 0.01
				document.documentElement.style.setProperty('--vh', `${vh}px`)
			}
			
			// Set initial viewport height
			setViewportHeight()
			
			// Update viewport height on resize
			window.addEventListener('resize', setViewportHeight)
			window.addEventListener('orientationchange', setViewportHeight)
			
			// Cleanup
			return () => {
				window.removeEventListener('resize', setViewportHeight)
				window.removeEventListener('orientationchange', setViewportHeight)
			}
		}
	}, [])
	
	return (
		<>
			<NetworkStatus />
			<TokenErrorHandler />
			<ErrorBoundary>
				<AuthErrorBoundary>
					<Gate />
				</AuthErrorBoundary>
			</ErrorBoundary>
		</>
	)
})

export default App
